import {
  Action,
  type GeneralComponentList,
  type GeneralComponentListKeys,
  type GlobalIdentifierObject,
  type LineItemComponentV2,
  type Rule,
  type Section
} from '../../../common/types';
import { type FormEvent, useEffect, useState } from 'react';
import { useAddRuleMutation, useUpdateRuleMutation } from '../receipt.api-slice';
import Breadcrumb from '../../../components/Breadcrumb';
import CustomTextInputV3 from '../../../components/Form/CustomTextInputV3';
import DeleteRule from '../components/ReceiptBuilderV2/DeleteRule';
import GeneralComponentBuilder from '../components/ReceiptBuilderV2/GeneralComponentBuilder';
import { getAgencyAndMerchantIdFromOwnerEntity } from '../../../common/helpers';
import GlobalIdentifiers from '../components/ReceiptBuilderV2/GlobalIdentifiers';
import HorizontalDivider from '../../devices/components/HorizontalDivider';
import LineItemComponentBuilder from '../components/ReceiptBuilderV2/LineItemComponentBuilder';
import { type OwnerEntityType } from '../../../common/constants';
import SectionBuilder from '../components/ReceiptBuilderV2/SectionBuilder';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface IProps {
  ownerEntityType: string;
  ownerEntity: OwnerEntityType;
  cancelUrl: string;
  rule: Rule;
  id?: string;
  name?: string;
}

const ReceiptRuleBuilderPage: React.FC<IProps> = ({
  ownerEntityType,
  ownerEntity,
  rule: { generalComponents, globalIdentifiers, id, lineItemComponents, name, sections }
}) => {
  const navigate = useNavigate();
  const [addResource] = useAddRuleMutation();
  const [updateRule, { isSuccess }] = useUpdateRuleMutation();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation();
  const [currentRuleName, setCurrentRuleName] = useState<string>(name);
  const [currentGeneralComponents, setCurrentGeneralComponents] = useState<GeneralComponentList>(generalComponents);
  const [currentGlobalIdentifiers, setCurrentGlobalIdentifiers] = useState<GlobalIdentifierObject[]>(globalIdentifiers);
  const [currentSections, setCurrentSections] = useState<Section[]>(sections);
  const [currentLineItemComponents, setCurrentLineItemComponents] = useState<LineItemComponentV2[]>(lineItemComponents);

  const toggleDeleteModal = (): void => {
    setOpenDeleteModal((prev) => !prev);
  };

  const handleGeneralComponentChange = (componentName: string, tokens: string[]): void => {
    const componentKey = componentName as GeneralComponentListKeys;
    setCurrentGeneralComponents((prevState) => ({
      ...prevState,
      [componentName]: {
        ...prevState[componentKey],
        tokens
      }
    }));
  };

  const handleGlobalIdentifierChange = (action: Action, value: GlobalIdentifierObject): void => {
    if (action === Action.ADD) {
      setCurrentGlobalIdentifiers([...currentGlobalIdentifiers, value]);
    }

    if (action === Action.MODIFY) {
      setCurrentGlobalIdentifiers((prevState) =>
        prevState.map((identifier) => (identifier.id === value.id ? value : identifier))
      );
    }

    if (action === Action.REMOVE) {
      setCurrentGlobalIdentifiers((prevState) => prevState.filter((identifier) => identifier.id !== value.id));
    }
  };

  const handleRuleNameChange = (name: string, value: string): void => {
    if (value !== '') {
      setCurrentRuleName(value);
    }
  };

  const handleSectionChange = (action: Action, value: Section): void => {
    if (action === Action.ADD) {
      setCurrentSections([...currentSections, value]);
    }

    if (action === Action.MODIFY) {
      setCurrentSections((prevState) => prevState.map((section) => (section.id === value.id ? value : section)));
    }

    if (action === Action.REMOVE) {
      setCurrentSections((prevState) => prevState.filter((section) => section.id !== value.id));
    }
  };

  const handleLineItemChange = (action: Action, lineItemComponent: LineItemComponentV2): void => {
    if (action === Action.ADD) {
      // need to prevent duplication
      const lineItemAlreadyExists = currentLineItemComponents.find((item) => item.name === lineItemComponent.name);
      if (lineItemAlreadyExists == null) {
        setCurrentLineItemComponents([...currentLineItemComponents, lineItemComponent]);
      } else {
        toast.warning('LineItem with this name already exists');
      }
    }

    if (action === Action.MODIFY) {
      setCurrentLineItemComponents((prevState) =>
        prevState.map((componentInLoop) =>
          componentInLoop.id === lineItemComponent.id ? lineItemComponent : componentInLoop
        )
      );
    }

    if (action === Action.REMOVE) {
      setCurrentLineItemComponents((prevState) =>
        prevState.filter((componentInLoop) => componentInLoop.id !== lineItemComponent.id)
      );
    }
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    if (currentRuleName === '') {
      toast.warning('Rule name is required');
      return;
    }

    if (currentSections.length === 0) {
      toast.warning('At least one section is required');
      return;
    }

    if (currentLineItemComponents.length === 0) {
      toast.warning('At least one line item component is required');
      return;
    }
    const { agencyId, merchantId } = getAgencyAndMerchantIdFromOwnerEntity(ownerEntityType, ownerEntity);
    const commonRuleObject: Rule = {
      name: currentRuleName,
      generalComponents: currentGeneralComponents,
      globalIdentifiers: currentGlobalIdentifiers,
      sections: currentSections,
      lineItemComponents: currentLineItemComponents
    };
    if (id != null) {
      await updateRule({ id: id, body: commonRuleObject });
    } else {
      const ruleObj = {
        ...commonRuleObject,
        ownerEntityType,
        ownerEntityId: ownerEntity.id,
        agencyId,
        merchantId
      };
      void addResource(ruleObj);
      toast('Rule is successfully attached');
    }
  };

  const handleReOrderSection = (sourceIndex: number, destinationIndex: number): void => {
    const result = Array.from(currentSections);
    const [removed] = result.splice(sourceIndex, 1);
    result.splice(destinationIndex, 0, removed);

    setCurrentSections(result);
  };

  const handleReOrderLineItem = (sourceIndex: number, destinationIndex: number): void => {
    const result = Array.from(currentLineItemComponents);
    const [removed] = result.splice(sourceIndex, 1);
    result.splice(destinationIndex, 0, removed);

    const reorderedLineItems = result.map((result, index) => ({
      ...result,
      rule: { ...result.rule, order: index + 1 }
    }));

    setCurrentLineItemComponents(reorderedLineItems);
  };

  useEffect(() => {
    if (isSuccess) {
      const message = 'Receipt rule is successfully updated.';
      toast(message);
    }
  }, [isSuccess]);

  return (
    <div>
      <div className="pb-4">
        <h1>{t('receiptRule.add.title')}</h1>
        <Breadcrumb items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('receiptRule.test.title') }]} />
      </div>
      <div className="w-full flex justify-between items-center">
        <div className="flex w-full h-full text-2xl text-center">
          {id === undefined ? t('receiptRule.add.title') : t('receiptRule.edit.title')}
        </div>
        {id != null && (
          <div className="w-1/2 flex justify-end items-center">
            <button
              onClick={() => {
                navigate(`/admin/rules/${id}/test`);
              }}
              className="ml-4 px-5 py-2 rounded-lg bg-primary text-white"
            >
              {t('receiptRule.add.testBtnLabel')}
            </button>
            <button onClick={toggleDeleteModal} className="ml-4 px-5 py-2 rounded-lg bg-red-800 text-white">
              {t('receiptRule.delete.title')}
            </button>
          </div>
        )}
      </div>
      <div className="w-full flex flex-col justify-between items-center">
        <CustomTextInputV3
          type="text"
          name="receipt-rule-name"
          value={currentRuleName}
          label="Rule Name"
          handleChange={handleRuleNameChange}
        />
        <GeneralComponentBuilder components={currentGeneralComponents} handleChange={handleGeneralComponentChange} />
        <HorizontalDivider />
        <GlobalIdentifiers identifiers={currentGlobalIdentifiers} handleChange={handleGlobalIdentifierChange} />
        <HorizontalDivider />
        <SectionBuilder
          sections={currentSections}
          handleChange={handleSectionChange}
          handleReOrder={handleReOrderSection}
        />
        <HorizontalDivider />
        <LineItemComponentBuilder
          lineItemComponent={currentLineItemComponents}
          handleChange={handleLineItemChange}
          handleReOrder={handleReOrderLineItem}
        />

        <div className="flex space-x-2 mt-4 w-full">
          <button onClick={handleSubmit} type="submit" className="w-24 text-white px-4 py-2 rounded-lg bg-primary">
            Submit
          </button>
        </div>
        {id != null && <DeleteRule isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} id={id} />}
      </div>
    </div>
  );
};

export default ReceiptRuleBuilderPage;
